import { debounce, throttle, capitalize } from "lodash"

const Hook = (HOOK_NAME, HOOK_PROPS) => ({
  refs: [],
  values: {},
  targets: [],
  actions: [],

  ...HOOK_PROPS,

  _valuesChangedObserver: null,

  mounted() {
    this.el.__hook = this
    this._listenersAbortController = new AbortController()
    this._listenedNodes = new WeakMap()
    this._valuesSetPending = {}
    this._refParents = []
    this._mountedCallbacks = []
    this._initRefs()
    this._initValues()
    this._initValuesChanged()
    this._initTargets()
    this._initActions()
    this._initHandleEventToSelf()
    this._initAncestors()
    HOOK_PROPS.mounted?.call(this)

    const mountedCallbacks = this._mountedCallbacks
    this._mountedCallbacks = null
    mountedCallbacks.forEach((callback) => callback())
  },

  updated() {
    this._initValues()
    this._initTargets()
    this._initActions()
    HOOK_PROPS.updated?.call(this)
  },

  destroyed() {
    this._destroyed = true
    if (this.el.__hook === this) delete this.el.__hook

    this._valuesChangedObserver?.disconnect()
    this._listenersAbortController?.abort()
    this._destroyRefs()
    HOOK_PROPS.destroyed?.call(this)
  },

  classAdd(className, node = this.el) {
    this.js().addClass(node, className)
    return this
  },

  classRemove(className, node = this.el) {
    this.js().removeClass(node, className)
    return this
  },

  classToggle(className, node = this.el) {
    this.js().toggleClass(node, className)
    return this
  },

  attributeSet(name, value, node = this.el) {
    this.js().setAttribute(node, name, value)
    return this
  },

  attributeRemove(name, node = this.el) {
    this.js().removeAttribute(node, name)
    return this
  },

  _initRefs() {
    this.refs.forEach((refName) => {
      let refExists = false
      const attributeName = `data-${HOOK_NAME}-${refName}-ref`
      const refSelector = this.el.getAttribute(attributeName)
      const nodes = refSelector
        ? document.querySelectorAll(refSelector)
        : this.el.querySelectorAll(`[data-${HOOK_NAME}-ref="${refName}"]`)

      nodes.forEach((refEl) => {
        if (refEl.__hook) {
          refExists = true
          this._addRef(refName, refEl.__hook)
        } else {
          // hook not mounted at the time of ref init, transfer its initiation on to ref itself
          refEl.__hookTransferredRefs = refEl.__hookTransferredRefs || []
          refEl.__hookTransferredRefs.push({ el: this.el, refName })
        }
      })

      if (!refExists) {
        delete this[`${refName}Ref`]
        delete this[`${refName}Refs`]
      }
    })

    // init transferred refs
    if (this.el.__hookTransferredRefs) {
      this.el.__hookTransferredRefs.forEach(({ el, refName }) => {
        el.__hook?._addRef(refName, this)
      })

      delete this.el.__hookTransferredRefs
    }
  },

  // sets this.[name]Ref & collects this.[name]Refs,
  // calls this.[name]RefMounted once per ref hook instance, after the `mounted()`
  // of whichever hook (this or the ref) is still mounting, so both are fully set up
  _addRef(refName, ref) {
    const refs = this[`${refName}Refs`] || []
    this[`${refName}Ref`] = ref

    if (!refs.includes(ref)) {
      this[`${refName}Refs`] = [...refs, ref]
      ref._refParents.push({ hook: this, refName })

      const callback = () => this[`${refName}RefMounted`]?.(ref)
      if (ref._mountedCallbacks) ref._callMounted(callback)
      else this._callMounted(callback)
    }
  },

  // removes a destroyed ref from this.[name]Refs, points this.[name]Ref to the
  // latest remaining ref (or removes it), calls this.[name]RefDestroyed
  _removeRef(refName, ref) {
    if (this._destroyed) return

    const refs = (this[`${refName}Refs`] || []).filter((r) => r !== ref)

    if (refs.length) this[`${refName}Refs`] = refs
    else delete this[`${refName}Refs`]

    if (this[`${refName}Ref`] === ref) {
      if (refs.length) this[`${refName}Ref`] = refs[refs.length - 1]
      else delete this[`${refName}Ref`]
    }

    this[`${refName}RefDestroyed`]?.(ref)
  },

  // detaches this hook from hooks it's a ref of, and from its own refs
  _destroyRefs() {
    this._refParents.forEach(({ hook, refName }) =>
      hook._removeRef(refName, this)
    )
    this._refParents = []

    this.refs.forEach((refName) => {
      const refs = this[`${refName}Refs`] || []
      refs.forEach((ref) => {
        ref._refParents = ref._refParents.filter(({ hook }) => hook !== this)
      })
    })
  },

  // registers this hook as a ref of ancestor hooks it's a ref of, and
  // re-inits targets & actions of ancestor hooks addressed by this hook's nodes,
  // so they pick up nodes rendered after their mount without their `updated()`
  // (e.g. by child LiveViews)
  _initAncestors() {
    let el = this.el.parentElement?.closest(`[phx-hook]`)
    while (el) {
      const name = el.getAttribute(`phx-hook`)

      const refName = this.el.getAttribute(`data-${name}-ref`)
      if (refName && el.__hook?.refs.includes(refName))
        el.__hook._addRef(refName, this)

      const selector = `[data-${name}-action], [data-${name}-target]`

      if (
        el.__hook &&
        (this.el.matches(selector) || this.el.querySelector(selector))
      ) {
        el.__hook._initTargets()
        el.__hook._initActions()
      }

      el = el.parentElement?.closest(`[phx-hook]`)
    }
  },

  // inits this.[name]Target(s) props,
  // calls this.[name]TargetDestroyed / this.[name]TargetMounted for removed / new target nodes
  _initTargets() {
    this.targets.forEach((targetName) => {
      const prevTargets = new Set(this[`${targetName}Targets`] || [])
      const targets = [
        ...this.el.querySelectorAll(
          `[data-${HOOK_NAME}-target="${targetName}"]`
        ),
      ]
      const targetsSet = new Set(targets)

      this[`${targetName}Targets`] = targets
      this[`${targetName}Target`] = targets[0]

      prevTargets.forEach((target) => {
        if (!targetsSet.has(target))
          this[`${targetName}TargetDestroyed`]?.(target)
      })

      targets.forEach((target) => {
        if (!prevTargets.has(target))
          this._callMounted(() => this[`${targetName}TargetMounted`]?.(target))
      })
    })
  },

  // inits this.[name]Action methods
  _initActions() {
    // actions by attribute:

    const attributeName = `data-${HOOK_NAME}-action`

    const nodes = [...this.el.querySelectorAll(`[${attributeName}]`)]
    if (this.el.matches(`[${attributeName}]`)) nodes.push(this.el)

    nodes.forEach((node) => {
      const listenerName = node.getAttribute(attributeName)?.trim()
      if (!listenerName) return
      // const { eventName, fnName, isPassive } = this._parseActionParams(params)

      const listener = this[`${listenerName}Action`]
      if (typeof listener !== `function`) return

      this._addListener(node, `attribute:${listenerName}`, `click`, (e) =>
        listener.call(this, e, node)
      )
    })

    // actions by config:

    this.actions.forEach((action) => {
      let nodes = [this.el]
      if (action.target) nodes = [this[`${action.target}Target`]]
      else if (action.targets) nodes = this[`${action.targets}Targets`]
      nodes = nodes.filter(Boolean)

      const listener = this[`${action.name}Action`]
      if (typeof listener !== `function`) return

      const actionOptions = {
        capture: action.capture,
        once: action.once,
        passive: action.passive,
      }
      const actionId = JSON.stringify({
        name: action.name,
        target: action.target ?? action.targets,
        type: action.type,
        debounce: action.debounce,
        throttle: action.throttle,
        options: actionOptions,
      })

      nodes.forEach((node) => {
        let callback = (e) => listener.call(this, e, node)
        if (action.debounce) callback = debounce(callback, action.debounce)
        else if (action.throttle) callback = throttle(callback, action.throttle)

        this._addListener(
          node,
          `config:${actionId}`,
          action.type,
          callback,
          actionOptions
        )
      })
    })
  },

  // adds an event listener once per hook instance, node and id;
  // listeners are removed (and pending debounced/throttled calls cancelled) on destroy
  _addListener(node, id, type, callback, options = {}) {
    let ids = this._listenedNodes.get(node)
    if (!ids) this._listenedNodes.set(node, (ids = new Set()))
    if (ids.has(id)) return
    ids.add(id)

    const { signal } = this._listenersAbortController
    if (callback.cancel)
      signal.addEventListener(`abort`, () => callback.cancel(), { once: true })

    node.addEventListener(type, callback, { ...options, signal })
  },

  // inits this.[name]Value props
  // inits this.[name]ValueChanged methods
  _initValues() {
    Object.entries(this.values).forEach(([valueName, valueType]) => {
      const valueKeyName = `${valueName}Value`
      const attributeName = `data-${HOOK_NAME}-${valueName}-value`
      if (Object.hasOwn(this, valueKeyName)) return // already initialized

      Object.defineProperty(this, valueKeyName, {
        set(newValue) {
          if (
            (valueType === String && typeof newValue !== `string`) ||
            (valueType === Boolean && typeof newValue !== `boolean`) ||
            (valueType === Number && typeof newValue !== `number`) ||
            (valueType === Object && typeof newValue !== `object`) ||
            (valueType === Array && !Array.isArray(newValue))
          ) {
            console.error(
              `Wrong value type on "${HOOK_NAME}" hook for "${valueName}" value. Expected ${valueType.name}, got ${capitalize(typeof newValue)}.`
            )
          }

          const oldValue = this.el.getAttribute(attributeName)
          const newValueEncoded = this._encodeValue(newValue, valueType)

          if (oldValue != newValueEncoded) {
            this.attributeSet(attributeName, newValueEncoded)

            const callbackFn = this[`${valueName}ValueChanged`]
            if (typeof callbackFn === `function`) {
              // let the values observer know this change is already handled here
              if (this._valuesChangedObserver) {
                const key = attributeName.toLowerCase()
                this._valuesSetPending[key] ||= []
                this._valuesSetPending[key].push([
                  oldValue,
                  String(newValueEncoded),
                ])
              }

              callbackFn.call(
                this,
                newValue,
                this._decodeValue(oldValue, valueType)
              )
            }
          }
        },
        get() {
          return this._decodeValue(
            this.el.getAttribute(attributeName),
            valueType
          )
        },
      })
    })
  },

  // observes value attribute changes and triggers corresponding `[name]ValueChanged` callbacks
  _initValuesChanged() {
    const valueAttributeNames = Object.keys(this.values)
      .filter((valueName) => {
        const callbackFn = this[`${valueName}ValueChanged`]
        return typeof callbackFn === `function`
      })
      .map(
        (valueName) =>
          `data-${HOOK_NAME.toLowerCase()}-${valueName.toLowerCase()}-value`
      )

    if (valueAttributeNames.length) {
      this._valuesChangedObserver = new MutationObserver((mutations) => {
        const setPending = this._valuesSetPending
        this._valuesSetPending = {}

        mutations.forEach((mutation, index) => {
          const { attributeName } = mutation
          const nextMutation = mutations
            .slice(index + 1)
            .find((m) => m.attributeName === attributeName)
          const newValue = nextMutation
            ? nextMutation.oldValue
            : this.el.getAttribute(attributeName)

          // attribute re-set to the same value (e.g. sticky JS ops re-applied after patches)
          if (newValue === mutation.oldValue) return

          // change already handled by the value setter
          const [pendingOld, pendingNew] = setPending[attributeName]?.[0] || []
          if (pendingOld === mutation.oldValue && pendingNew === newValue) {
            setPending[attributeName].shift()
            return
          }

          const valueNameRaw = mutation.attributeName.match(
            new RegExp(`data-${HOOK_NAME.toLowerCase()}-(.*)-value`)
          )?.[1]

          const [valueName, valueType] = Object.entries(this.values).find(
            ([name]) => name.toLowerCase() === valueNameRaw
          )

          const callbackFn = this[`${valueName}ValueChanged`]
          if (typeof callbackFn === `function`) {
            callbackFn.call(
              this,
              this._decodeValue(newValue, valueType),
              this._decodeValue(mutation.oldValue, valueType)
            )
          }
        })
      })

      this._valuesChangedObserver.observe(this.el, {
        attributes: true,
        attributeOldValue: true,
        attributeFilter: valueAttributeNames,
      })
    }
  },

  // calls handleEventToSelf(name, data) on socket |> push_event_to_self(name, data) execution
  _initHandleEventToSelf() {
    const callbackFn = this.handleEventToSelf
    if (typeof callbackFn === `function`) {
      this.handleEvent(`pushed_event_to_self`, (params) => {
        if (params.dom_id == this.el.id)
          callbackFn.call(this, params.name, params.data)
      })
    }
  },

  // calls `*Mounted` callbacks right away, or right after this hook's own
  // `mounted()` when called while mounting, so they can rely on its state
  _callMounted(callback) {
    if (this._mountedCallbacks) this._mountedCallbacks.push(callback)
    else callback()
  },

  _decodeValue(value, type) {
    let newValue

    if (type === Boolean) {
      newValue = !(value == `0` || value == `false`)
    } else if (type === Number) {
      newValue = value == null ? null : Number(value.replace(/_/g, ``))
    } else if (type === Object || type === Array) {
      newValue = JSON.parse(value)
    } else {
      newValue = value
    }

    return newValue
  },

  _encodeValue(value, type) {
    let newValue

    if (type === Boolean) {
      newValue = value.toString()
    } else if (type === Object || type === Array) {
      newValue = JSON.stringify(value)
    } else {
      newValue = value
    }

    return newValue
  },

  // _parseActionParams(params) {
  //   // fetch
  //   // fetch:passive
  //   // click->fetch
  //   // click->fetch:passive
  //   let type, listener, options

  //   let splitted = params.split(`->`)
  //   if (splitted.length == 2) {
  //     // ->
  //     type = splitted[0]
  //     splitted = splitted.split(`:`)
  //     if (splitted.length == 2) {
  //       // :
  //       listener = splitted[0]
  //       options = splitted[1]
  //     } else listener =
  //   } else {
  //   }

  //   return {}
  // },

  pushEventToSelf(...args) {
    this.pushEventTo(this.el, ...args)
  },

  putFlash({ type, text }) {
    this.pushEvent(`put_flash`, { type, text })
  },
})

export default Hook
